import React, { useCallback, useEffect, useRef, useState } from "react";
import { apiGet, apiPost, getApiUrl } from "../../utils/apiClient";

type NormRect = { x: number; y: number; w: number; h: number };

type Props = {
  clipId: string | null;
  /** Used when re-running the macro pipeline after re-ingest. */
  matchId?: string;
  busy?: boolean;
  /** Deletes old extracted frames, re-ingests with saved calibration, then runs the pipeline for `matchId`. */
  onReingestAndRun?: () => Promise<void>;
};

const MacroCalibrationTool: React.FC<Props> = ({ clipId, matchId, busy, onReingestAndRun }) => {
  const [previewT, setPreviewT] = useState(480);
  const [zoom, setZoom] = useState(1.5);
  const [mode, setMode] = useState<"ocr" | "minimap">("ocr");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [ocrRect, setOcrRect] = useState<NormRect | null>(null);
  const [minimapRect, setMinimapRect] = useState<NormRect | null>(null);
  const [calMessage, setCalMessage] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });

  /** Drag in canvas pixel space (same as canvas.width/height). */
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const dragCurrentRef = useRef<{ x: number; y: number } | null>(null);
  const rafRef = useRef<number | null>(null);

  const loadExisting = useCallback(async () => {
    if (!clipId) return;
    try {
      const data = await apiGet(`/api/macro/calibration?clipId=${encodeURIComponent(clipId)}`);
      if (data.calibration?.ocr) setOcrRect(data.calibration.ocr as NormRect);
      if (data.calibration?.minimap) setMinimapRect(data.calibration.minimap as NormRect);
    } catch {
      // ignore
    }
  }, [clipId]);

  useEffect(() => {
    void loadExisting();
  }, [loadExisting]);

  /** New preview URL → reset until `onLoad` so wrapper width matches the next frame. */
  useEffect(() => {
    if (previewUrl) setImgNatural({ w: 0, h: 0 });
  }, [previewUrl]);

  const canvasCoords = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const r = canvas.getBoundingClientRect();
    const x = ((clientX - r.left) / r.width) * canvas.width;
    const y = ((clientY - r.top) / r.height) * canvas.height;
    return { x, y };
  };

  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imgNatural.w) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const cw = canvas.width;
    const ch = canvas.height;
    ctx.clearRect(0, 0, cw, ch);

    const draw = (r: NormRect, color: string, label: string) => {
      const x = r.x * cw;
      const y = r.y * ch;
      const w = r.w * cw;
      const h = r.h * ch;
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(2, Math.round(3 * (cw / 1920)));
      ctx.strokeRect(x, y, w, h);
      ctx.fillStyle = color;
      ctx.font = `${Math.round(14 * (cw / 1920)) + 10}px sans-serif`;
      ctx.fillText(label, x + 4, y + Math.min(22, ch * 0.03));
    };

    if (ocrRect) draw(ocrRect, "rgba(234, 179, 8, 0.95)", "OCR");
    if (minimapRect) draw(minimapRect, "rgba(34, 197, 94, 0.95)", "MINIMAP");

    const start = dragStartRef.current;
    const cur = dragCurrentRef.current;
    if (start && cur) {
      const x1 = Math.min(start.x, cur.x);
      const y1 = Math.min(start.y, cur.y);
      const w = Math.abs(cur.x - start.x);
      const h = Math.abs(cur.y - start.y);
      ctx.setLineDash([6, 4]);
      ctx.strokeStyle = mode === "ocr" ? "rgba(234, 179, 8, 0.85)" : "rgba(34, 197, 94, 0.85)";
      ctx.lineWidth = Math.max(2, Math.round(2 * (cw / 1920)));
      ctx.strokeRect(x1, y1, w, h);
      ctx.setLineDash([]);
    }
  }, [ocrRect, minimapRect, mode, imgNatural.w]);

  const scheduleRedraw = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      redrawCanvas();
    });
  }, [redrawCanvas]);

  useEffect(() => {
    scheduleRedraw();
  }, [scheduleRedraw, previewUrl, ocrRect, minimapRect, zoom, imgNatural.w]);

  const onImageLoad = () => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;
    setImgNatural({ w: img.naturalWidth, h: img.naturalHeight });
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    scheduleRedraw();
  };

  const loadPreview = () => {
    if (!clipId) {
      setCalMessage("Select a clip first.");
      return;
    }
    const url = `${getApiUrl("/api/macro/preview-frame")}?clipId=${encodeURIComponent(clipId)}&t=${previewT}&cb=${Date.now()}`;
    setPreviewUrl(url);
    setCalMessage(null);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    dragStartRef.current = canvasCoords(e.clientX, e.clientY);
    dragCurrentRef.current = { ...dragStartRef.current };
    redrawCanvas();
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!dragStartRef.current) return;
    dragCurrentRef.current = canvasCoords(e.clientX, e.clientY);
    redrawCanvas();
  };

  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    try {
      (e.target as HTMLCanvasElement).releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    const canvas = canvasRef.current;
    const start = dragStartRef.current;
    if (!canvas || !start) return;
    const end = canvasCoords(e.clientX, e.clientY);
    dragStartRef.current = null;
    dragCurrentRef.current = null;

    const x1 = Math.min(start.x, end.x);
    const y1 = Math.min(start.y, end.y);
    const x2 = Math.max(start.x, end.x);
    const y2 = Math.max(start.y, end.y);
    const w = x2 - x1;
    const h = y2 - y1;
    if (w < 3 || h < 3) {
      redrawCanvas();
      return;
    }
    const norm: NormRect = {
      x: x1 / canvas.width,
      y: y1 / canvas.height,
      w: w / canvas.width,
      h: h / canvas.height,
    };
    if (mode === "ocr") setOcrRect(norm);
    else setMinimapRect(norm);
    setCalMessage(`${mode} region updated. Save calibration when done.`);
    redrawCanvas();
  };

  const saveCalibration = async () => {
    if (!clipId) {
      setCalMessage("No clipId.");
      return;
    }
    if (!ocrRect && !minimapRect) {
      setCalMessage("Draw at least one rectangle.");
      return;
    }
    try {
      await apiPost("/api/macro/calibration", {
        clipId,
        ocr: ocrRect ?? undefined,
        minimap: minimapRect ?? undefined,
      });
      setCalMessage("Saved. Use “Rerun ingest & pipeline” below to apply to all frames.");
    } catch (err) {
      setCalMessage(err instanceof Error ? err.message : "Save failed");
    }
  };

  if (!clipId) {
    return (
      <section className="macro-calibration">
        <h2>Region calibration</h2>
        <p className="macro-muted">Upload or fetch a YouTube clip first, then pick OCR + minimap regions on a frame.</p>
      </section>
    );
  }

  const wrapStyle: React.CSSProperties =
    imgNatural.w > 0
      ? {
          position: "relative",
          display: "inline-block",
          width: imgNatural.w * zoom,
          maxWidth: "none",
        }
      : { position: "relative", display: "inline-block" };

  return (
    <section className="macro-calibration">
      <h2>Region calibration</h2>
      <p className="macro-muted">
        Load <strong>one</strong> preview frame to place boxes. Saved regions are <strong>normalized</strong> (0–1 of width/height) and{" "}
        <strong>applied to every sampled frame</strong> in the video—the preview time only helps you align; it does not limit which frames use
        OCR/minimap. Zoom for precision, then drag for OCR (clock) and minimap. <strong>Saving</strong> merges with the calibration file on disk:
        regions you do not redraw in this session are kept, so you can update OCR and minimap in separate saves.
      </p>
      <div className="macro-calibration-controls">
        <label>
          Time (s){" "}
          <input
            type="number"
            min={0}
            step={1}
            value={previewT}
            onChange={(e) => setPreviewT(Number(e.target.value))}
          />
        </label>
        <button type="button" onClick={loadPreview}>
          Load preview frame
        </button>
        <label className="macro-cal-zoom">
          Zoom{" "}
          <input
            type="range"
            min={0.5}
            max={5}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
          <span>{zoom.toFixed(2)}×</span>
        </label>
        <span>Draw:</span>
        <label>
          <input type="radio" name="calmode" checked={mode === "ocr"} onChange={() => setMode("ocr")} /> OCR
        </label>
        <label>
          <input type="radio" name="calmode" checked={mode === "minimap"} onChange={() => setMode("minimap")} /> Minimap
        </label>
        <button type="button" onClick={saveCalibration}>
          Save calibration
        </button>
        {onReingestAndRun ? (
          <button
            type="button"
            disabled={busy || !clipId}
            title="Removes old extracted frames & debug images, re-runs ingest with the saved calibration file, then runs the macro pipeline for the current Match ID."
            onClick={() => void onReingestAndRun()}
          >
            {busy ? "Working…" : "Rerun ingest & pipeline"}
          </button>
        ) : null}
      </div>
      {matchId ? (
        <p className="macro-muted macro-cal-pipeline-note">
          Pipeline uses Match ID <code>{matchId}</code> (same as the main “Run Macro Pipeline” control).
        </p>
      ) : null}
      {calMessage ? <p className="macro-cal-hint">{calMessage}</p> : null}
      {previewUrl ? (
        <div className="macro-calibration-scroll">
          <div className="macro-calibration-zoom-inner" style={wrapStyle}>
            <img
              ref={imgRef}
              src={previewUrl}
              alt="Preview"
              onLoad={onImageLoad}
              className="macro-calibration-img"
              draggable={false}
            />
            <canvas
              ref={canvasRef}
              className="macro-calibration-canvas"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            />
          </div>
        </div>
      ) : null}
      <div className="macro-calibration-summary">
        <div>OCR: {ocrRect ? JSON.stringify(ocrRect) : "not set"}</div>
        <div>Minimap: {minimapRect ? JSON.stringify(minimapRect) : "not set"}</div>
      </div>
    </section>
  );
};

export default MacroCalibrationTool;
